'use client'

import { useState } from 'react'
import usePinchZoom from 'react-use-pinch-zoom'
import {
  IconArrowNarrowLeft,
  IconArrowNarrowRight,
  IconX
} from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'

import styles from './Magnifier.module.scss'

const asDisabled = (disabled: boolean) =>
  ({ disabled }) as React.HTMLAttributes<HTMLDivElement>

function Magnifier({
  src,
  alt,
  showControls,
  onPrevClick,
  onNextClick,
  currentIndex,
  imagesCount
}: {
  src: string
  alt: string
  showControls: boolean
  onPrevClick: () => void
  onNextClick: () => void
  currentIndex: number
  imagesCount: number
}) {
  const [[x, y], setXY] = useState([0, 0])
  const [[imgWidth, imgHeight], setSize] = useState([0, 0])
  const [showMagnifier, setShowMagnifier] = useState(false)
  const [open, setOpen] = useState(false)
  const [containerProps, contentProps] = usePinchZoom<
    HTMLDivElement,
    HTMLImageElement
  >()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const magnifierHeight = 150
  const magnifierWidth = 150
  const zoomLevel = 2.5

  return (
    <>
      <div
        className={styles.wrapper}
        onMouseEnter={e => {
          const elem = e.currentTarget
          const { width, height } = elem.getBoundingClientRect()

          setSize([width, height])
          setShowMagnifier(true)
        }}
        onMouseMove={e => {
          const elem = e.currentTarget
          const { top, left } = elem.getBoundingClientRect()

          const xPos = e.pageX - left - window.pageXOffset
          const yPos = e.pageY - top - window.pageYOffset

          setXY([xPos, yPos])
        }}
        onMouseLeave={() => {
          setShowMagnifier(false)
        }}
      >
        <Image
          className={styles.image}
          onClick={handleOpen}
          alt={alt}
          src={src}
          width={500}
          height={500}
          style={{ height: '500px', width: 'auto', cursor: 'pointer' }}
        />
        <div
          className={styles.magnifier}
          style={{
            display: showMagnifier ? '' : 'none',
            position: 'absolute',
            borderRadius: '100%',
            pointerEvents: 'none',
            height: `${magnifierHeight}px`,
            width: `${magnifierWidth}px`,
            top: `${y - magnifierHeight / 2}px`,
            left: `${x - magnifierWidth / 2}px`,
            opacity: '1',
            border: '1px solid lightgray',
            backgroundColor: 'white',
            backgroundImage: `url('${src}')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${imgWidth * zoomLevel}px ${
              imgHeight * zoomLevel
            }px`,
            backgroundPositionX: `${-x * zoomLevel + magnifierWidth / 2}px`,
            backgroundPositionY: `${-y * zoomLevel + magnifierHeight / 2}px`
          }}
        />
      </div>
      {open && (
        <div
          onClick={handleClose}
          {...containerProps}
          className={styles.preview}
        >
          <IconX
            className={styles.closeIcon}
            onClick={handleClose}
            size={30}
          />
          {showControls && (
            <>
              <div
                {...asDisabled(currentIndex === 0)}
                className={clsx(styles.arrow, {
                  [styles.arrowDisabled]: currentIndex === 0
                })}
                onClick={onPrevClick}
              >
                <IconArrowNarrowLeft />
              </div>
              <div
                {...asDisabled(currentIndex >= imagesCount - 1)}
                className={clsx(styles.arrow, styles.arrowRight, {
                  [styles.arrowDisabled]: currentIndex >= imagesCount - 1
                })}
                onClick={onNextClick}
              >
                <IconArrowNarrowRight />
              </div>
            </>
          )}
          <Image
            {...contentProps}
            onClick={e => e.stopPropagation()}
            alt={alt}
            src={src}
            width={500}
            height={500}
            style={{ height: '95vh', width: 'auto', touchAction: 'auto' }}
          />
        </div>
      )}
    </>
  )
}

export default Magnifier
